import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Send, CheckCircle, Mail, Calendar, FileText, Users } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ContactInfoPanel } from '../components/ContactInfoPanel';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' }
};

export default function ContactPage() {
  const { language, t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null); // 'success', 'error', null
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
    request_type: 'contact'
  });

  // Set request type from URL params
  useEffect(() => {
    const type = searchParams.get('type');
    // Map trial to demo
    const mappedType = type === 'trial' ? 'demo' : type;
    if (mappedType && ['demo', 'quote', 'contact', 'visitor'].includes(mappedType)) {
      setFormData(prev => ({ ...prev, request_type: mappedType }));
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, {
        ...formData,
        language
      });
      setStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        subject: '',
        message: '',
        request_type: 'contact'
      });
    } catch (err) {
      setStatus('error');
    } finally {
      setLoading(false);
      setTimeout(() => setStatus(null), 5000);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const requestTypes = [
    { id: 'contact', label: t('contact.form.types.contact'), icon: Mail },
    { id: 'demo', label: t('contact.form.types.demo'), icon: Calendar },
    { id: 'quote', label: t('contact.form.types.quote'), icon: FileText },
    { id: 'visitor', label: t('contact.form.types.visitor'), icon: Users }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      <SEO
        title={language === 'de' ? 'Kontakt - E-Learning Beratung & Demo' : 'Contact - E-Learning Consulting & Demo'}
        description={language === 'de' 
          ? 'Kontaktieren Sie e-Matrix Innovations für E-Learning Beratung, kostenlose Demos und individuelle Angebote. Arbeitssicherheit, Compliance, Corporate Training in Österreich.' 
          : 'Contact e-Matrix Innovations for e-learning consulting, free demos and custom offers. Workplace safety, compliance, corporate training in Austria.'}
        keywords={language === 'de'
          ? 'E-Learning Beratung Österreich, E-Learning Demo, Arbeitssicherheit Angebot, Unterweisungssystem anfragen, Corporate Training Österreich, e-Matrix Kontakt'
          : 'E-Learning Consulting Austria, E-Learning Demo, Workplace Safety Quote, Training System Inquiry, Corporate Training Austria, e-Matrix Contact'}
        path="/contact"
        lang={language}
      />
      <section className="py-16 md:py-20 bg-[#E7F0FA]" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0D2440] mb-6">
              {t('contact.title')}
            </h1>
            <p className="text-lg md:text-xl text-[#5A7A9B]">
              {t('contact.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-16">
            {/* Contact Info */}
            <motion.div {...fadeUp} className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-[#0D2440] mb-8">
                {language === 'de' ? 'Kontakt' : 'Contact'}
              </h2>

              <ContactInfoPanel language={language} />
            </motion.div>

            {/* Form */}
            <motion.div
              {...fadeUp}
              className="lg:col-span-2 bg-[#E7F0FA] rounded-2xl p-8 md:p-12"
            >
              {status === 'success' ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#0D2440] mb-4">
                    {language === 'de' ? 'Vielen Dank!' : 'Thank You!'}
                  </h3>
                  <p className="text-[#5A7A9B]">
                    {t('contact.form.success')}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  {/* Request Type Selection - at top of form */}
                  <div>
                    <h3 className="font-semibold text-[#0D2440] mb-4">
                      {t('contact.form.requestType')}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {requestTypes.map((type) => (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, request_type: type.id }))}
                          className={`flex items-center space-x-2 p-3 rounded-xl border transition-all ${
                            formData.request_type === type.id
                              ? 'border-[#2E5E99] bg-[#2E5E99]/5 text-[#2E5E99]'
                              : 'border-[#C8D8E8] hover:border-[#2E5E99]/50'
                          }`}
                          data-testid={`request-type-${type.id}`}
                        >
                          <type.icon className="w-5 h-5" />
                          <span className="text-sm font-medium">{type.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-[#0D2440]">
                        {t('contact.form.name')} *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-2"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-[#0D2440]">
                        {t('contact.form.email')} *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-2"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="company" className="text-[#0D2440]">
                        {t('contact.form.company')}
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="mt-2"
                        data-testid="contact-company-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-[#0D2440]">
                        {t('contact.form.phone')}
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="mt-2"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-[#0D2440]">
                      {t('contact.form.subject')} *
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="mt-2"
                      data-testid="contact-subject-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[#0D2440]">
                      {t('contact.form.message')} *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="mt-2"
                      data-testid="contact-message-input"
                    />
                  </div>

                  {status === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
                      {t('contact.form.error')}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#0D2440] hover:bg-[#162D4A] text-white h-12 text-base"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      <span className="animate-spin w-5 h-5 border-2 border-white/30 border-t-white rounded-full" />
                    ) : (
                      <>
                        {t('contact.form.submit')}
                        <Send className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
